import { useMemo, Fragment } from 'react';
import tw, { TwStyle } from 'twin.macro';

import { ISegment, ParagraphData } from '@insights_module/types/Episode';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { useEpisodeUrl } from '@insights_module/hooks/useEpisodeUrl';
import { useTranscript } from '@insights_module/hooks/useTranscript';
import { AudioPlayer } from '@insights_module/components/episode_details_screen/AudioPlayer';
import { GarmRiskLevelType, CategoryType } from '@insights_module/types/BrandSafety';
import { GARM_RISK_LEVEL } from '@insights_module/constants/garm';
import { Loader } from '@components/Loader';
import { convertSecondsToHMS } from '@utils/dateUtils';

import { SpeakerLabel } from '@insights_module/components/episode_details_screen/SpeakerLabel';
import { TranscriptGarmLabels } from '@insights_module/components/episode_details_screen/TranscriptGarmLabels';
import { TranscriptContextualLabels } from '@insights_module/components/episode_details_screen/TranscriptContextualLabels';

const Container = tw.div`overflow-y-scroll p-[24px] pb-[100px] h-[calc(100% - 42px)] relative bg-neutral-background`;
const ParagraphsContainer = tw.div`rounded-[4px] [&>div]:border-l-[3px] [&>div]:border-solid [&>div]:border-transparent`;
const ParagraphWrapper = tw.div`flex items-start justify-start rounded-[4px]`;
const Time = tw.p`text-accent-1 py-[12px] pr-0 ml-4 text-[14px] leading-[165%]`;
const StyledParagraph = tw.p`text-text-and-icon text-left p-[12px] pl-0 ml-4 text-[16px] leading-[150%] tracking-[0.16px]`;
const PlayerContainer = tw.div`w-full absolute bottom-0 left-0 p-4`;
const NoTranscriptFallback = tw.div`w-full h-full flex items-center justify-center`;
const Separator = tw.div`w-full h-[1px] bg-gray-1 my-2 mx-2`;

const BRAND_SAFETY_STYLE_MAPPER: { [key: string]: TwStyle } = {
  [GARM_RISK_LEVEL.unrelated]: tw`[&>div]:border-transparent` as TwStyle,
  [GARM_RISK_LEVEL.low]: tw`[&>div]:border-garm-low-bg` as TwStyle,
  [GARM_RISK_LEVEL.medium]: tw`[&>div]:border-garm-medium-bg` as TwStyle,
  [GARM_RISK_LEVEL.high]: tw`[&>div]:border-garm-high-bg` as TwStyle,
  [GARM_RISK_LEVEL.floor]: tw`[&>div]:border-floor-bg` as TwStyle,
};

const TranscriptParagraph = ({
  paragraph,
  segmentId,
  index,
}: {
  paragraph: ParagraphData;
  segmentId: number;
  index: number;
}) => {
  const { activeView, currentAudio, showTranscriptTimestamps, showSpeakerLabel } =
    useEpisodeDetailsContext();

  const isCurrentAudio =
    currentAudio?.paragraph?.startTime === paragraph.startTime &&
    !!currentAudio?.paragraph?.endTime &&
    currentAudio?.paragraph?.endTime <= paragraph.endTime;

  return (
    <Fragment key={index}>
      {activeView === 'speakers' && showSpeakerLabel && (
        <SpeakerLabel speaker={paragraph.speaker} />
      )}
      <ParagraphWrapper css={[isCurrentAudio && tw`bg-sec-4`]}>
        {!!showTranscriptTimestamps && (
          <Time>{convertSecondsToHMS(Number(paragraph.startTime))}</Time>
        )}
        <StyledParagraph data-time={paragraph.startTime} data-index={`${segmentId}-${index}`}>
          {paragraph.paragraphString}
        </StyledParagraph>
      </ParagraphWrapper>
    </Fragment>
  );
};

const Transcript = ({ item }: { item: ISegment }) => {
  const { activeView, selectedGarmCategory, selectedContextualCategory } =
    useEpisodeDetailsContext();

  if (!item) return null;

  const hasGarmRisk: null | GarmRiskLevelType =
    activeView === 'garm_brand_safety' &&
    item.garm[selectedGarmCategory as CategoryType] !== ('Unrelated' as GarmRiskLevelType)
      ? item.garm[selectedGarmCategory as CategoryType]
      : null;

  const hasContextual: boolean = useMemo(() => {
    if (
      activeView !== 'iab_categories' &&
      (!item.contextual.categories || !selectedContextualCategory)
    )
      false;

    return !!item?.contextual?.categories?.some((item) => item.name === selectedContextualCategory);
  }, [item?.contextual?.categories, selectedContextualCategory]);

  return (
    <div id={`segment-${item.id}`}>
      {activeView === 'garm_brand_safety' && <TranscriptGarmLabels garmTracebility={item.garm} />}
      {activeView === 'iab_categories' && (
        <TranscriptContextualLabels contextual={item.contextual} />
      )}

      <ParagraphsContainer
        css={[
          hasContextual && tw`[&>div]:border-sec-4`,
          hasGarmRisk && BRAND_SAFETY_STYLE_MAPPER[hasGarmRisk.toLowerCase()],
        ]}>
        <div>
          {item?.paragraphData?.map((paragraph, index) => {
            return (
              <TranscriptParagraph
                key={index}
                index={index}
                segmentId={item.id}
                paragraph={paragraph}
              />
            );
          })}
        </div>
      </ParagraphsContainer>
    </div>
  );
};

export const EpisodeTranscript = ({ transcript }: { transcript: ISegment[] }) => {
  const { url } = useEpisodeUrl();
  const { isLoading, isError } = useTranscript();

  if (isLoading) {
    return (
      <Container>
        <Loader size='md' theme='dark' />
      </Container>
    );
  }

  if (!isLoading && isError) {
    return <NoTranscriptFallback>No Transcript</NoTranscriptFallback>;
  }

  return (
    <>
      <Container id='transcript'>
        {transcript.map((item: ISegment, index) => (
          <Fragment key={item.startTime}>
            <Transcript key={item.startTime} item={item} />
            {index !== transcript.length - 1 && <Separator />}
          </Fragment>
        ))}
      </Container>
      <PlayerContainer>
        <AudioPlayer url={url} />
      </PlayerContainer>
    </>
  );
};
